import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, CreditCard, Building, CheckSquare, Square } from 'lucide-react';
import { Account, CreateAccountRequest, UpdateAccountRequest } from '../../types/account';
import { Client } from '../../types/client';
import { accountService } from '../../services';

interface AccountModalProps {
  account?: Account | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (account: Account) => void;
}

export const AccountModal: React.FC<AccountModalProps> = ({
  account,
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<CreateAccountRequest>({
    account_name: '',
    client_id: 0,
    auto_assign: false,
    is_active: true
  });

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const isEditing = !!account;

  // Load available clients
  useEffect(() => {
    if (isOpen) {
      loadClients();
    }
  }, [isOpen]);

  // Initialize form data when account changes
  useEffect(() => {
    if (account) {
      setFormData({
        account_name: account.account_name,
        client_id: account.client_id,
        auto_assign: account.auto_assign,
        is_active: account.is_active
      });
    } else {
      setFormData({
        account_name: '',
        client_id: 0,
        auto_assign: false,
        is_active: true
      });
    }
    setError(null);
    setFieldErrors({});
  }, [account, isOpen]);

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const response = await accountService.getAvailableClients();
      if (response.data) {
        setClients(response.data);
        // Set first client as default for new accounts
        if (!account && response.data.length > 0) {
          setFormData(prev => ({ ...prev, client_id: response.data![0].id }));
        }
      }
    } catch (err: any) {
      setError('Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  };

  const handleInputChange = (field: keyof CreateAccountRequest, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear field errors when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string[]> = {};

    if (!formData.account_name.trim()) {
      errors.account_name = ['Account name is required'];
    }

    if (!formData.client_id || formData.client_id === 0) {
      errors.client_id = ['Please select a client'];
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;

      if (isEditing && account) {
        response = await accountService.updateAccount(account.id, formData as UpdateAccountRequest);
      } else {
        response = await accountService.createAccount(formData);
      }

      if (response.data) {
        onSave(response.data);
        onClose();
      } else {
        setError(response.message || 'Failed to save account');
      }
    } catch (err: any) {
      if (err.response?.data?.errors) {
        setFieldErrors(err.response.data.errors);
      } else {
        setError(err.response?.data?.message || 'Failed to save account');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = clients.find(c => c.id === formData.client_id);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-lg p-0 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <CreditCard className="h-6 w-6 text-primary-600" />
              <h3 className="text-lg font-medium text-gray-900">
                {isEditing ? 'Edit Account' : 'Create New Account'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* Account Name */}
            <div>
              <label className="label flex items-center space-x-2">
                <CreditCard className="h-4 w-4 text-gray-500" />
                <span>Account Name *</span>
              </label>
              <input
                type="text"
                value={formData.account_name}
                onChange={(e) => handleInputChange('account_name', e.target.value)}
                className={`input ${fieldErrors.account_name ? 'border-red-300' : ''}`}
                placeholder="Enter account name"
                required
              />
              {fieldErrors.account_name && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.account_name[0]}</p>
              )}
            </div>

            {/* Client Assignment */}
            <div>
              <label className="label flex items-center space-x-2">
                <Building className="h-4 w-4 text-gray-500" />
                <span>Assign to Client *</span>
              </label>
              {loadingClients ? (
                <div className="flex items-center space-x-2 py-2">
                  <div className="loading-spinner" />
                  <span className="text-gray-500">Loading clients...</span>
                </div>
              ) : (
                <select
                  value={formData.client_id}
                  onChange={(e) => handleInputChange('client_id', Number(e.target.value))}
                  className={`input ${fieldErrors.client_id ? 'border-red-300' : ''}`}
                  required
                >
                  <option value={0}>Select a client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.client_name}
                      {!client.is_active && ' (Inactive)'}
                    </option>
                  ))}
                </select>
              )}
              {fieldErrors.client_id && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.client_id[0]}</p>
              )}
              {selectedClient && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm text-blue-800">
                    <div className="font-medium">{selectedClient.client_name}</div>
                    {selectedClient.contact_email && (
                      <div className="text-xs mt-1">{selectedClient.contact_email}</div>
                    )}
                    <div className={`text-xs mt-1 ${selectedClient.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedClient.is_active ? 'Active' : 'Inactive'} Client
                      {!selectedClient.is_active && ' - Cannot assign new accounts to inactive clients'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Auto Assign Setting */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <button
                  type="button"
                  onClick={() => handleInputChange('auto_assign', !formData.auto_assign)}
                  className={`flex-shrink-0 transition-colors ${
                    formData.auto_assign ? 'text-primary-600' : 'text-gray-400'
                  }`}
                >
                  {formData.auto_assign ? (
                    <CheckSquare className="h-5 w-5" />
                  ) : (
                    <Square className="h-5 w-5" />
                  )}
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Auto-assign transactions
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    When enabled, transactions matching this account pattern will be automatically
                    assigned during CSV processing. When disabled, manual assignment is required.
                  </p>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Active Account
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-7">
              Inactive accounts cannot receive new transactions or employee assignments
            </p>
          </form>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.account_name.trim() || !formData.client_id || (selectedClient && !selectedClient.is_active)}
              className="btn btn-primary flex items-center space-x-2"
            >
              {loading && <div className="loading-spinner" />}
              <Save className="w-4 h-4" />
              <span>{isEditing ? 'Update' : 'Create'} Account</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};